import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Stepper, Step, StepLabel, StepButton, Button, Paper } from "@mui/material";
import {
  useVehicleTypes,
  useSaveVehicle,
} from "../../../hooks/useVehicleQuery";
import {
  useNotification,
  NotificationSeverity,
} from "../../../context/NotificationContext";
import { useLayout } from "../../../context/LayoutContext";
import VehicleTypeSelector from "../../../components/vehicle/VehicleTypeSelector";
import VehicleInfoSection from "../../../components/vehicle/VehicleInfoSection";
import ContactSectionAddVehicle from "../../../components/vehicle/ContactSectionAddVehicle";
import DocumentsSection from "../../../components/vehicle/DocumentsSection";
import LoadingAnimation from "../../../components/loading/LoadingAnimation";
import "../../../assets/scss/views/AddVehicle.scss";
import { useOperations } from "../../../hooks/useDocumentationVehicleQuery";

import GridViewIcon from "../../../assets/svg/view/grid_view.svg";
import GridViewLightIcon from "../../../assets/svg/view/grid_view_light.svg";
import SplitViewIcon from "../../../assets/svg/view/split_view.svg";
import SplitViewLightIcon from "../../../assets/svg/view/split_view_light.svg";
import { useVehicleFormCache } from "../../../hooks/vehicle/useVehicleFormCache";
import CacheClearButton from "../../../components/ui/btn/CacheClearButton";

export default function AddVehicle() {
  const { theme } = useLayout();
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const { mutateAsync: saveVehicle, isLoading: isSaving } = useSaveVehicle();
  const { data: vehicleTypesData } = useVehicleTypes();
  const { data: operationsData } = useOperations({ limit: 100 });

  // Hook de caché
  const { 
    saveToCache, 
    loadFromCache, 
    clearCache, 
    isCacheLoaded, 
    hasCachedData 
  } = useVehicleFormCache();

  // Estado para vista dual/simple (solo para paso 2 y 4)
  const [viewMode, setViewMode] = useState("dual");
  const [currentInfoView, setCurrentInfoView] = useState("info");

  // Estado para rastrear pasos completados
  const [completedSteps, setCompletedSteps] = useState(new Set());

  // Estado principal para datos del vehículo con nuevas propiedades
  const [vehicleData, setVehicleData] = useState(() => {
    const defaultData = {
      main: {
        info: {
          vehicleTypeId: "",
          plate: "",
          brand: "",
          model: "",
          capacity: "",
          year: "",
          limitedNotifications: true,
          ownFleet: true,
        },
        contact: {
          operatorId: "",
          ownerId: "",
          primaryPhoneNumber: "",
          primaryPhoneCountryId: "",
          secondaryPhoneNumber: "",
          secondaryPhoneCountryId: "",
          contractStartDate: null,
          contractEndDate: null,
          nationalityId: "",
        },
        documents: [],
      },
      trailer: {
        info: {
          vehicleTypeId: "",
          plate: "",
          brand: "",
          model: "",
          capacity: "",
          year: "",
          limitedNotifications: true,
          ownFleet: true,
        },
        contact: {
          operatorId: "",
          ownerId: "",
          primaryPhoneNumber: "",
          primaryPhoneCountryId: "",
          secondaryPhoneNumber: "",
          secondaryPhoneCountryId: "",
          contractStartDate: null,
          contractEndDate: null,
          nationalityId: "",
        },
        documents: [],
      },
      belongsToOperation: false,
      oprationId: "",
      isTrailerRequired: false,
      isMainVehicleTrailer: false,
    };
    return defaultData;
  });

  // Estados para control de flujo y UI
  const [activeStep, setActiveStep] = useState(0);
  const [needsTrailer, setNeedsTrailer] = useState(false);
  const [selectedOperationId, setSelectedOperationId] = useState("");
  const [hasChanges, setHasChanges] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingCache, setIsLoadingCache] = useState(true);

  // Efecto para cargar datos desde caché al inicializar
  useEffect(() => {
    const loadCachedData = async () => {
      try {
        const cachedFormState = loadFromCache();
        
        if (cachedFormState && isCacheLoaded) {
          setVehicleData(cachedFormState.vehicleData);
          setActiveStep(cachedFormState.activeStep || 0);
          setNeedsTrailer(cachedFormState.needsTrailer || false);
          setSelectedOperationId(cachedFormState.selectedOperationId || "");
          setCompletedSteps(new Set(cachedFormState.completedSteps) || new Set());
          setHasChanges(cachedFormState.hasChanges || false);
          setViewMode(cachedFormState.viewMode || "dual");
          setCurrentInfoView(cachedFormState.currentInfoView || "info");

          if (cachedFormState.hasChanges) {
            safeShowNotification(
              "Se han restaurado los datos guardados del formulario",
              NotificationSeverity.INFO
            );
          }
        }
      } catch (error) {
        console.error('Error al cargar datos desde caché:', error);
        safeShowNotification(
          "Error al restaurar datos guardados",
          NotificationSeverity.WARNING
        );
      } finally {
        setIsLoadingCache(false);
      }
    };

    loadCachedData();
  }, [loadFromCache, isCacheLoaded]);

  // Efecto para guardar cambios en caché
  useEffect(() => {
    if (!isLoadingCache && isCacheLoaded) {
      const formState = {
        vehicleData,
        activeStep,
        needsTrailer,
        selectedOperationId,
        completedSteps: Array.from(completedSteps),
        hasChanges,
        viewMode,
        currentInfoView
      };

      saveToCache(formState);
    }
  }, [
    vehicleData,
    activeStep,
    needsTrailer,
    selectedOperationId,
    completedSteps,
    hasChanges,
    viewMode,
    currentInfoView,
    saveToCache,
    isLoadingCache,
    isCacheLoaded
  ]);

  // Funciones auxiliares mejoradas
  const isVehicleTypeTrailer = (vehicleTypeId) => {
    if (!vehicleTypeId || !vehicleTypesData?.data) return false;
    
    const selectedType = vehicleTypesData.data.find(
      (type) => type.id === vehicleTypeId
    );
    
    if (!selectedType) return false;

    const criteria = [
      selectedType.component_type === "Remolque",
      selectedType.category?.includes("Remolque"),
      selectedType.name?.includes("REMOLQUE"),
      selectedType.name?.includes("TRAILER"),
      selectedType.type?.toLowerCase().includes("remolque"),
      selectedType.category?.toLowerCase().includes("trailer")
    ];

    return criteria.some(criterion => criterion === true);
  };

  const doesVehicleTypeRequireTrailer = (vehicleTypeId) => {
    if (!vehicleTypeId || !vehicleTypesData?.data) return false;
    if (isVehicleTypeTrailer(vehicleTypeId)) return false;
    const selectedType = vehicleTypesData.data.find(
      (type) => type.id === vehicleTypeId
    );
    return (
      selectedType?.category?.includes("Cabezote") ||
      selectedType?.name?.includes("Cama baja") ||
      selectedType?.name?.includes("TRACTOCAMION") ||
      false
    );
  };

  const isVehicleTypeCabezote = (vehicleTypeId) => {
    if (!vehicleTypeId || !vehicleTypesData?.data) return false;
    if (isVehicleTypeTrailer(vehicleTypeId)) return false;
    const selectedType = vehicleTypesData.data.find(
      (type) => type.id === vehicleTypeId
    );
    return selectedType?.category?.includes("Cabezote") || false;
  };

  // Función auxiliar para obtener reglas de validación
  const getValidationRulesForVehicleType = (vehicleTypeId, section = 'main') => {
    const isTrailer = section === 'trailer' || 
                     (section === 'main' && isVehicleTypeTrailer(vehicleTypeId));
    
    return {
      requiresOperator: !isTrailer,
      requiresPhoneNumber: !isTrailer,
      requiresOperation: isVehicleTypeCabezote(vehicleTypeId) || isTrailer,
      isTrailer
    };
  };

  // Efecto mejorado para detección de tipo de vehículo
  useEffect(() => {
    if (vehicleData.main.info.vehicleTypeId && vehicleTypesData?.data) {
      const mainVehicleTypeId = vehicleData.main.info.vehicleTypeId;
      
      const isMainTrailer = isVehicleTypeTrailer(mainVehicleTypeId);
      const requiresTrailer = !isMainTrailer && doesVehicleTypeRequireTrailer(mainVehicleTypeId);
      
      setNeedsTrailer(requiresTrailer);
      setVehicleData((prev) => ({
        ...prev,
        isTrailerRequired: requiresTrailer,
        isMainVehicleTrailer: isMainTrailer,
      }));
    }
  }, [vehicleData.main.info.vehicleTypeId, vehicleTypesData]);

  // Pasos actualizados
  const steps = needsTrailer
    ? [
        "Tipo de Vehículo",
        "Información del Cabezote",
        "Documentación",
        "Información del Remolque",
        "Documentación del Remolque",
      ]
    : [
        "Tipo de Vehículo",
        "Información del Vehículo",
        "Documentación",
      ];

  // Función para limpiar caché y resetear formulario
  const handleClearCache = useCallback(async () => {
    try {
      const success = clearCache();
      
      if (success) {
        setVehicleData({
          main: {
            info: {
              vehicleTypeId: "",
              plate: "",
              brand: "",
              model: "",
              capacity: "",
              year: "",
              limitedNotifications: true,
              ownFleet: true,
            },
            contact: {
              operatorId: "",
              ownerId: "",
              primaryPhoneNumber: "",
              primaryPhoneCountryId: "",
              secondaryPhoneNumber: "",
              secondaryPhoneCountryId: "",
              contractStartDate: null,
              contractEndDate: null,
              nationalityId: "",
            },
            documents: [],
          },
          trailer: {
            info: {
              vehicleTypeId: "",
              plate: "",
              brand: "",
              model: "",
              capacity: "",
              year: "",
              limitedNotifications: true,
              ownFleet: true,
            },
            contact: {
              operatorId: "",
              ownerId: "",
              primaryPhoneNumber: "",
              primaryPhoneCountryId: "",
              secondaryPhoneNumber: "",
              secondaryPhoneCountryId: "",
              contractStartDate: null,
              contractEndDate: null,
              nationalityId: "",
            },
            documents: [],
          },
          belongsToOperation: false,
          oprationId: "",
          isTrailerRequired: false,
          isMainVehicleTrailer: false,
        });
        
        setActiveStep(0);
        setNeedsTrailer(false);
        setSelectedOperationId("");
        setCompletedSteps(new Set());
        setHasChanges(false);
        setViewMode("dual");
        setCurrentInfoView("info");
        
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error al limpiar caché:', error);
      return false;
    }
  }, [clearCache]);

  // Handlers para vista
  const handleToggleViewMode = () => {
    setViewMode((prevMode) => (prevMode === "simple" ? "dual" : "simple"));
  };

  // Handlers para datos
  const handleVehicleInfoChange = (section, field, value) => {
    setVehicleData((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        info: {
          ...prev[section].info,
          [field]: value,
        },
      },
    }));
    setHasChanges(true);
  };

  const handleContactInfoChange = (section, field, value) => {
    setVehicleData((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        contact: {
          ...prev[section].contact,
          [field]: value,
        },
      },
    }));
    setHasChanges(true);
  };

  const handleDocumentsChange = (section, action) => {
    switch (action.type) {
      case "ADD_DOCUMENT":
        setVehicleData((prev) => ({
          ...prev,
          [section]: {
            ...prev[section],
            documents: [
              ...prev[section].documents,
              {
                ...action.payload.document,
                docType: String(action.payload.document.docType),
                createdAt: new Date().toISOString(),
              },
            ],
          },
        }));
        break;

      case "UPDATE_DOCUMENT":
        setVehicleData((prev) => ({
          ...prev,
          [section]: {
            ...prev[section],
            documents: prev[section].documents.map((doc) =>
              doc.id === action.payload.docId
                ? {
                    ...doc,
                    [action.payload.field]:
                      action.payload.field === "docType"
                        ? String(action.payload.value)
                        : action.payload.value,
                    updatedAt: new Date().toISOString(),
                  }
                : doc
            ),
          },
        }));
        break;

      case "DELETE_DOCUMENT":
        setVehicleData((prev) => ({
          ...prev,
          [section]: {
            ...prev[section],
            documents: prev[section].documents.filter(
              (doc) => doc.id !== action.payload.docId
            ),
          },
        }));
        break;

      default:
        break;
    }
    setHasChanges(true);
  };

  const handleBelongsToOperationChange = (value) => {
    setVehicleData((prev) => ({
      ...prev,
      belongsToOperation: value,
    }));
    if (!value) {
      setSelectedOperationId("");
    }
    setHasChanges(true);
  };

  // Validar paso específico mejorado
  const validateSpecificStep = (stepIndex) => {
    const currentSection = stepIndex <= 2 || !needsTrailer ? "main" : "trailer";

    if (stepIndex >= 3 && !vehicleData.isTrailerRequired) {
      return true;
    }

    switch (stepIndex) {
      case 0:
        return !!vehicleData.main.info.vehicleTypeId;

      case 1:
      case 3:
        const info = vehicleData[currentSection]?.info || {};
        const contact = vehicleData[currentSection]?.contact || {};
        
        const validationRules = getValidationRulesForVehicleType(
          info.vehicleTypeId, 
          currentSection
        );
        
        if (!info.plate || !info.brand || !info.model) return false;
        
        if (validationRules.requiresOperator && !contact.operatorId) return false;
        if (validationRules.requiresPhoneNumber && 
            (!contact.primaryPhoneNumber || !contact.primaryPhoneCountryId)) return false;
        
        if (!contact.nationalityId) return false;

        return true;

      case 2:
      case 4:
        const currentVehicleTypeId = stepIndex === 2 ? 
          vehicleData.main.info.vehicleTypeId : 
          vehicleData.trailer.info.vehicleTypeId;
          
        const validationRulesDoc = getValidationRulesForVehicleType(
          currentVehicleTypeId, 
          currentSection
        );

        if (validationRulesDoc.requiresOperation && !selectedOperationId) {
          return false;
        }

        const documents = vehicleData[currentSection].documents;
        const requiredDocsCount = documents.filter((doc) => doc.required && !doc.file).length || 0;
        return requiredDocsCount === 0;

      default:
        return true;
    }
  };

  // Handler para navegación directa del Stepper
  const handleStepClick = (stepIndex) => {
    if (stepIndex <= activeStep || completedSteps.has(stepIndex)) {
      setActiveStep(stepIndex);
    } else {
      safeShowNotification(
        "Por favor complete los pasos anteriores primero",
        NotificationSeverity.WARNING
      );
    }
  };

  // Navegación
  const handleNext = () => {
    if (validateSpecificStep(activeStep)) {
      setCompletedSteps(prev => new Set(prev).add(activeStep));
    }
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleSkipTrailer = () => {
    setVehicleData((prev) => ({
      ...prev,
      isTrailerRequired: false,
    }));
    
    setCompletedSteps(prev => new Set(prev).add(3));
    setActiveStep(steps.length - 1);
    setHasChanges(true);
    
    showNotification({
      message: "Se ha omitido el registro del remolque",
      severity: NotificationSeverity.INFO,
      position: {
        vertical: "top",
        horizontal: "right",
      },
    });
  };

  // Validaciones y submit
  const safeShowNotification = (
    message,
    severity = NotificationSeverity.INFO
  ) => {
    try {
      showNotification({
        message: message || "Notificación del sistema",
        severity: severity || NotificationSeverity.INFO,
        position: {
          vertical: "top",
          horizontal: "right",
        },
      });
    } catch (error) {
      console.error("Error al mostrar notificación:", error);
      console.warn(message);
    }
  };

  const validateStep = () => {
    const currentSection =
      activeStep <= 2 || !needsTrailer ? "main" : "trailer";

    if (activeStep >= 3 && !vehicleData.isTrailerRequired) {
      return true;
    }

    switch (activeStep) {
      case 0:
        if (!vehicleData.main.info.vehicleTypeId) {
          safeShowNotification(
            "Por favor, seleccione un tipo de vehículo",
            NotificationSeverity.ERROR
          );
          return false;
        }
        return true;

      case 1:
      case 3:
        const info = vehicleData[currentSection]?.info || {};
        const contact = vehicleData[currentSection]?.contact || {};
        const missingFields = [];
        const missingContactFields = [];

        if (!info.plate) missingFields.push("Placa");
        if (!info.brand) missingFields.push("Marca");
        if (!info.model) missingFields.push("Modelo");

        const validationRules = getValidationRulesForVehicleType(
          info.vehicleTypeId, 
          currentSection
        );

        if (validationRules.requiresOperator && !contact.operatorId) {
          missingContactFields.push("Operador");
        }
        if (validationRules.requiresPhoneNumber) {
          if (!contact.primaryPhoneNumber) missingContactFields.push("Número de teléfono principal");
          if (!contact.primaryPhoneCountryId) missingContactFields.push("Código de país del teléfono principal");
        }

        if (!contact.nationalityId) missingContactFields.push("Nacionalidad");

        const allMissingFields = [...missingFields, ...missingContactFields];

        if (allMissingFields.length > 0) {
          safeShowNotification(
            `Por favor, complete los siguientes campos obligatorios: ${allMissingFields.join(", ")}`,
            NotificationSeverity.ERROR
          );
          return false;
        }
        return true;

      case 2:
      case 4:
        const currentVehicleTypeId = activeStep === 2 ? 
          vehicleData.main.info.vehicleTypeId : 
          vehicleData.trailer.info.vehicleTypeId;
          
        const validationRulesDoc = getValidationRulesForVehicleType(
          currentVehicleTypeId, 
          currentSection
        );

        if (validationRulesDoc.requiresOperation && !selectedOperationId) {
          safeShowNotification(
            "Por favor, seleccione una operación para este tipo de vehículo",
            NotificationSeverity.ERROR
          );
          return false;
        }

        const documents = vehicleData[currentSection].documents;
        const requiredDocsCount = documents.filter((doc) => doc.required && !doc.file).length || 0;

        if (requiredDocsCount > 0) {
          safeShowNotification(
            `Faltan ${requiredDocsCount} documento${requiredDocsCount === 1 ? "" : "s"} obligatorio${requiredDocsCount === 1 ? "" : "s"} por cargar`,
            NotificationSeverity.ERROR
          );
          return false;
        }
        return true;

      default:
        return true;
    }
  };

  const handleNextWithValidation = () => {
    if (validateStep()) {
      handleNext();
    }
  };

  const validateCompleteForm = () => {
    const errors = [];

    const isMainVehicleTrailer = vehicleData.isMainVehicleTrailer;

    if (!vehicleData.main.info.vehicleTypeId)
      errors.push("Tipo de vehículo no seleccionado");
    if (!vehicleData.main.info.plate)
      errors.push("Placa del vehículo principal no especificada");
    if (!vehicleData.main.info.brand)
      errors.push("Marca del vehículo principal no especificada");
    if (!vehicleData.main.info.model)
      errors.push("Modelo del vehículo principal no especificado");

    if (!isMainVehicleTrailer) {
      if (
        !vehicleData.main.contact.primaryPhoneNumber ||
        !vehicleData.main.contact.primaryPhoneCountryId
      ) {
        errors.push("Número de teléfono principal incompleto");
      }
      if (!vehicleData.main.contact.operatorId) {
        errors.push("Operador no asignado");
      }
    }

    if (!vehicleData.main.contact.nationalityId) {
      errors.push("Nacionalidad del vehículo principal no especificada");
    }

    const mainVehicleNeedsOperation = isVehicleTypeCabezote(
      vehicleData.main.info.vehicleTypeId
    ) || isMainVehicleTrailer;
    
    if (mainVehicleNeedsOperation && !selectedOperationId) {
      errors.push("Operación no seleccionada");
    }

    const mainRequiredDocs = vehicleData.main.documents.filter(
      (doc) => doc.required && !doc.file
    );
    if (mainRequiredDocs.length > 0)
      errors.push("Documentos obligatorios del vehículo principal pendientes");

    if (needsTrailer && vehicleData.isTrailerRequired) {
      if (!vehicleData.trailer.info.vehicleTypeId)
        errors.push("Tipo de remolque no seleccionado");
      if (!vehicleData.trailer.info.plate)
        errors.push("Placa del remolque no especificada");
      if (!vehicleData.trailer.info.brand)
        errors.push("Marca del remolque no especificada");
      if (!vehicleData.trailer.info.model)
        errors.push("Modelo del remolque no especificado");

      if (!vehicleData.trailer.contact.nationalityId) {
        errors.push("Nacionalidad del remolque no especificada");
      }

      const trailerRequiredDocs = vehicleData.trailer.documents.filter(
        (doc) => doc.required && !doc.file
      );
      if (trailerRequiredDocs.length > 0)
        errors.push("Documentos obligatorios del remolque pendientes");
    }

    return errors;
  };

  // Submit mejorado con is_trailer
  const handleSubmit = async () => {
    if (isSubmitting) return;

    try {
      setIsSubmitting(true);

      if (!validateStep()) {
        setIsSubmitting(false);
        return;
      }

      const validationErrors = validateCompleteForm();
      if (validationErrors.length > 0) {
        showNotification({
          message: `Por favor corrija los siguientes errores antes de continuar: ${validationErrors.join(
            ", "
          )}`,
          severity: NotificationSeverity.ERROR,
          position: {
            vertical: "top",
            horizontal: "right",
          },
        });
        setIsSubmitting(false);
        return;
      }

      const submitData = {
        vehicleTypeId: vehicleData.main.info.vehicleTypeId,
        plate: (vehicleData.main.info.plate || "").toUpperCase().trim(),
        brand: vehicleData.main.info.brand,
        model: (vehicleData.main.info.model || "").trim(),
        capacity: vehicleData.main.info.capacity || null,
        year: vehicleData.main.info.year || null,
        limitedNotifications: vehicleData.main.info.limitedNotifications,
        ownFleet: vehicleData.main.info.ownFleet,
        belongsToOperation: vehicleData.belongsToOperation,
        is_trailer: vehicleData.isMainVehicleTrailer,
        ownerId: vehicleData.main.contact.ownerId,
        operatorId: vehicleData.main.contact.operatorId || null,
        gpsProviderId: !vehicleData.isMainVehicleTrailer && 
                     vehicleData.main.info.vehicleTypeId !== 'fc52271b-d9a9-47ac-b734-8aaa38bf5edf' 
                     ? (vehicleData.main.info.gpsProviderId || null) 
                     : null,
        contactInfo: {
          primaryPhoneNumber: (
            vehicleData.main.contact.primaryPhoneNumber || ""
          ).trim(),
          primaryPhoneCountryId: vehicleData.main.contact.primaryPhoneCountryId,
          secondaryPhoneNumber:
            (vehicleData.main.contact.secondaryPhoneNumber || "").trim() ||
            null,
          secondaryPhoneCountryId:
            vehicleData.main.contact.secondaryPhoneCountryId || null,
          nationalityId: vehicleData.main.contact.nationalityId,
        },
        documents: vehicleData.main.documents.map((doc) => ({
          ...doc,
          docType: String(doc.docType || ""),
        })),
        contractStartDate: vehicleData.main.contact.contractStartDate,
        contractEndDate: vehicleData.main.contact.contractEndDate,
      };

      // Ajustar campos para remolques principales
      if (vehicleData.isMainVehicleTrailer) {
        submitData.operatorId = vehicleData.main.contact.operatorId || null;
        if (!submitData.contactInfo.primaryPhoneNumber) {
          submitData.contactInfo.primaryPhoneNumber = null;
          submitData.contactInfo.primaryPhoneCountryId = null;
        }
      }

      if (vehicleData.belongsToOperation && selectedOperationId) {
        submitData.operationId = selectedOperationId;
      }

      if (needsTrailer && vehicleData.isTrailerRequired) {
        submitData.trailer = {
          vehicleTypeId: vehicleData.trailer.info.vehicleTypeId,
          plate: (vehicleData.trailer.info.plate || "").toUpperCase().trim(),
          brand: vehicleData.trailer.info.brand,
          model: (vehicleData.trailer.info.model || "").trim(),
          capacity: vehicleData.trailer.info.capacity || null,
          year: vehicleData.trailer.info.year || null,
          limitedNotifications: vehicleData.trailer.info.limitedNotifications,
          ownFleet: vehicleData.trailer.info.ownFleet,
          belongsToOperation: vehicleData.belongsToOperation,
          ownerId:
            vehicleData.trailer.contact.ownerId ||
            vehicleData.main.contact.ownerId,
          operatorId: vehicleData.trailer.contact.operatorId || null,
          contactInfo: {
            primaryPhoneNumber: (
              vehicleData.trailer.contact.primaryPhoneNumber || ""
            ).trim(),
            primaryPhoneCountryId:
              vehicleData.trailer.contact.primaryPhoneCountryId,
            secondaryPhoneNumber:
              (vehicleData.trailer.contact.secondaryPhoneNumber || "").trim() ||
              null,
            secondaryPhoneCountryId:
              vehicleData.trailer.contact.secondaryPhoneCountryId || null,
            nationalityId: vehicleData.trailer.contact.nationalityId,
          },
          documents: vehicleData.trailer.documents.map((doc) => ({
            ...doc,
            docType: String(doc.docType || ""),
          })),
          contractStartDate: vehicleData.trailer.contact.contractStartDate,
          contractEndDate: vehicleData.trailer.contact.contractEndDate,
        };

        if (vehicleData.belongsToOperation && selectedOperationId) {
          submitData.trailer.operationId = selectedOperationId;
        }
      }

      if (needsTrailer) {
        submitData.trailerRequired = vehicleData.isTrailerRequired;
      }

      const response = await saveVehicle({ data: submitData });

      showNotification({
        message: "Vehículo registrado exitosamente",
        severity: NotificationSeverity.SUCCESS,
        position: {
          vertical: "top",
          horizontal: "right",
        },
      });
      
      clearCache();
      setHasChanges(false);
      navigate("/vehicles");
    } catch (error) {
      showNotification({
        message:
          error.response?.data?.message || "Error al registrar el vehículo",
        severity: NotificationSeverity.ERROR,
        position: {
          vertical: "top",
          horizontal: "right",
        },
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Renderizado condicional según el paso activo
  const renderStepContent = () => {
  let currentSection = "main";
  
  if (needsTrailer && activeStep >= 3) {
    currentSection = "trailer";
  }

  switch (activeStep) {
    case 0:
      return (
        <VehicleTypeSelector
          selectedTypeId={vehicleData.main.info.vehicleTypeId}
          onChange={(value) =>
            handleVehicleInfoChange("main", "vehicleTypeId", value)
          }
          theme={theme}
        />
      );

    case 1:
    case 3:
      //  Considerar tanto el paso como si es remolque principal
      const isTrailer = activeStep === 3 || 
                       (activeStep === 1 && vehicleData.isMainVehicleTrailer);
      const showViewToggle = true;

      return (
        <>
          {showViewToggle && (
            <div className={`GM__${theme}-view-toggle-container`}>
              <button
                className={`GM__${theme}-view-toggle-btn`}
                onClick={handleToggleViewMode}
                title={viewMode === "simple" ? "Vista dividida" : "Vista simple"}
              >
                <img
                  src={viewMode === "simple" ? 
                    (theme === "dark" ? SplitViewLightIcon : SplitViewIcon) : 
                    (theme === "dark" ? GridViewLightIcon : GridViewIcon)
                  }
                  alt={viewMode === "simple" ? "Vista dividida" : "Vista simple"}
                  className={`GM__${theme}-view-icon`}
                />
              </button>
            </div>
          )}

          {viewMode === "simple" && (
            <div className={`GM__${theme}-info-navigation`}>
              <div className={`GM__${theme}-nav-button-group`}>
                <button
                  className={`GM__${theme}-nav-button ${
                    currentInfoView === "info" ? "active" : ""
                  } left`}
                  onClick={() => setCurrentInfoView("info")}
                >
                  Información del {isTrailer ? "Remolque" : "Vehículo"}
                </button>
                <button
                  className={`GM__${theme}-nav-button ${
                    currentInfoView === "contact" ? "active" : ""
                  } right`}
                  onClick={() => setCurrentInfoView("contact")}
                >
                  Información de Contacto
                </button>
              </div>
            </div>
          )}

          {viewMode === "simple" ? (
            <div className={`GM__${theme}-simple-content`}>
              {currentInfoView === "info" ? (
                <VehicleInfoSection
                  vehicleInfo={vehicleData[currentSection].info}
                  onChange={(field, value) =>
                    handleVehicleInfoChange(currentSection, field, value)
                  }
                  theme={theme}
                  isTrailer={isTrailer} 
                />
              ) : (
                <ContactSectionAddVehicle
                  contactInfo={vehicleData[currentSection].contact}
                  onContactInfoChange={(field, value) =>
                    handleContactInfoChange(currentSection, field, value)
                  }
                  theme={theme}
                  isTrailer={isTrailer}
                />
              )}
            </div>
          ) : (
            <div className={`GM__${theme}-dual-content`}>
              <div className={`GM__${theme}-dual-panel left`}>
                <VehicleInfoSection
                  vehicleInfo={vehicleData[currentSection].info}
                  onChange={(field, value) =>
                    handleVehicleInfoChange(currentSection, field, value)
                  }
                  theme={theme}
                  isTrailer={isTrailer}
                />
              </div>
              <div className={`GM__${theme}-dual-panel right`}>
                <ContactSectionAddVehicle
                  contactInfo={vehicleData[currentSection].contact}
                  onContactInfoChange={(field, value) =>
                    handleContactInfoChange(currentSection, field, value)
                  }
                  theme={theme}
                  isTrailer={isTrailer}
                />
              </div>
            </div>
          )}
        </>
      );

    case 2:
      return (
        <DocumentsSection
          vehicleTypeId={vehicleData.main.info.vehicleTypeId}
          operationId={selectedOperationId}
          onOperationChange={setSelectedOperationId}
          documents={vehicleData.main.documents}
          onDocumentsChange={(action) =>
            handleDocumentsChange("main", action)
          }
          onBelongsToOperationChange={handleBelongsToOperationChange}
          theme={theme}
          operations={operationsData?.data || []}
          requiresOperation={isVehicleTypeCabezote(vehicleData.main.info.vehicleTypeId)}
          allowChangeOperation={true}
          isTrailer={vehicleData.isMainVehicleTrailer}
          isMainVehicle={true}
        />
      );

    case 4:
      return (
        <DocumentsSection
          vehicleTypeId={vehicleData.trailer.info.vehicleTypeId || ''} 
          operationId={selectedOperationId}
          onOperationChange={setSelectedOperationId}
          documents={vehicleData.trailer.documents}
          onDocumentsChange={(action) =>
            handleDocumentsChange("trailer", action)
          }
          theme={theme}
          operations={operationsData?.data || []}
          requiresOperation={false} 
          allowChangeOperation={false}
          isTrailer={true}
          isMainVehicle={true}
        />
      );

    default:
      return <div>Paso no definido</div>;
  }
};
  // Evento para confirmar antes de abandonar la página
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasChanges) {
        e.preventDefault();
        e.returnValue = "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasChanges]);

  // Mostrar loading mientras se carga el caché
  if (isLoadingCache) {
    return (
      <div className={`GM__${theme}-add-vehicle-container`}>
        <div className={`GM__${theme}-loading-cache`}>
          <LoadingAnimation />
          <p>Cargando datos del formulario...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`GM__${theme}-add-vehicle-container`}>
      {isSubmitting && <LoadingAnimation />}

      <CacheClearButton
        onClearCache={handleClearCache}
        theme={theme}
        hasCachedData={hasCachedData}
        showNotification={(notification) => showNotification({
          ...notification,
          severity: notification.severity || NotificationSeverity.INFO,
          position: {
            vertical: "top",
            horizontal: "right",
          },
        })}
      />

      <Paper elevation={3} className={`GM__${theme}-vehicle-form-container`}>
        <Stepper
          activeStep={activeStep}
          alternativeLabel
          className={`GM__${theme}-stepper`}
          nonLinear
        >
          {steps.map((label, index) => {
            const stepProps = {};
            const labelProps = {};
            
            const isCompleted = completedSteps.has(index);
            const isClickable = index <= activeStep || isCompleted;
            
            if (isCompleted) {
              stepProps.completed = true;
            }
            
            return (
              <Step key={label} {...stepProps}>
                <StepButton
                  onClick={() => handleStepClick(index)}
                  disabled={!isClickable}
                  className={`GM__${theme}-step-button ${isClickable ? 'clickable' : ''}`}
                >
                  <StepLabel {...labelProps}>{label}</StepLabel>
                </StepButton>
              </Step>
            );
          })}
        </Stepper>

        <div className={`GM__${theme}-step-content`}>{renderStepContent()}</div>

        <div className={`GM__${theme}-navigation-buttons`}>
          <Button
            disabled={activeStep === 0}
            onClick={handleBack}
            className={`GM__${theme}-back-button`}
          >
            Atrás
          </Button>

          {needsTrailer && activeStep === 3 && (
            <Button
              variant="outlined"
              color="secondary"
              onClick={handleSkipTrailer}
              className={`GM__${theme}-skip-button`}
            >
              Omitir Trailer
            </Button>
          )}

          {activeStep === steps.length - 1 ? (
            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmit}
              disabled={isSubmitting}
              className={`GM__${theme}-submit-button`}
            >
              {isSubmitting ? "Procesando..." : "Registrar Vehículo"}
            </Button>
          ) : (
            <Button
              variant="contained"
              color="primary"
              onClick={handleNextWithValidation}
              className={`GM__${theme}-next-button`}
            >
              Siguiente
            </Button>
          )}
        </div>
      </Paper>
    </div>
  );
}